/**
 * «Техника» карточки хозяйства — парк единиц (AK-747, wireframe экран 5).
 *
 * Хозяин вкладки — инженер по технике, и вопрос, на который она отвечает,
 * ровно один: «что у меня есть и где стоит». Расчёт нагрузки здесь НЕ живёт —
 * «успеем ли» считает экран выполнимости плана по агросрокам, и дублировать
 * его цифрами в списке значит завести два ответа на один вопрос.
 *
 * Под-вкладки «Сцепки» и «ТО» — заглушки. Сцепка живёт на уровне холдинга
 * (`company_machinery_units` без `farm_id`), собирать её из карточки ОДНОГО
 * хозяйства нельзя: получилось бы два места для одной сущности. Поэтому
 * колонка «Сцепки» в таблице — только справка «в N сцепках».
 */
import { useMemo } from 'react';
import { Archive, ArchiveRestore, CheckCircle2, FileSpreadsheet, MoveRight, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Stack } from '@/components/ui/stack';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { batchLimitNotice } from '@/lib/bulk/batchLimits';
import { MachineryUnitsPanel } from './units/MachineryUnitsPanel';
import {
  MACHINERY_GROUP_LABELS,
  fleetFreshnessLabel,
  machineryBreakdown,
  machineryGroupLabel,
  unitCountLabel,
  type FleetFreshness,
  type MachineryCounts,
  type MachineryRow,
  type MachineryView,
} from './machineryLogic';
import { splitWriteOffReason } from './writeOffReason';

const NUM = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

export function MachineryTab({
  farmId,
  view,
  onViewChange,
  rows,
  total,
  counts,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  search,
  onSearchChange,
  group,
  onGroupChange,
  includeArchived,
  onIncludeArchivedChange,
  selected,
  onToggleRow,
  onToggleAll,
  canWrite,
  batchMax,
  busy,
  onAdd,
  onImport,
  onArchive,
  onRestore,
  onReassign,
  freshness,
  confirming,
  onConfirmFleet,
}: {
  farmId: string;
  view: MachineryView;
  onViewChange: (view: MachineryView) => void;
  rows: readonly MachineryRow[];
  total: number;
  counts: MachineryCounts | undefined;
  loading: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  group: string | null;
  onGroupChange: (value: string | null) => void;
  includeArchived: boolean;
  onIncludeArchivedChange: (value: boolean) => void;
  selected: ReadonlySet<string>;
  onToggleRow: (id: string) => void;
  onToggleAll: (ids: string[], checked: boolean) => void;
  canWrite: boolean;
  batchMax: number;
  busy: boolean;
  onAdd: () => void;
  /** Второй способ завести парк — списком из файла (AK-804). */
  onImport: () => void;
  onArchive: () => void;
  onRestore: () => void;
  onReassign: () => void;
  /** Свежесть парка (Р29) — шапка «Актуальность парка … [Подтвердить]». */
  freshness: FleetFreshness | undefined;
  confirming: boolean;
  onConfirmFleet: () => void;
}) {
  const ids = useMemo(() => rows.map((r) => r.id), [rows]);
  const allChecked = ids.length > 0 && ids.every((id) => selected.has(id));
  const someChecked = !allChecked && ids.some((id) => selected.has(id));
  const chosen = rows.filter((r) => selected.has(r.id));
  const limitNotice = batchLimitNotice(chosen.length, ids.length, batchMax);
  const breakdown = machineryBreakdown(counts);
  // Кнопки пачки — по СОСТАВУ выбора, а не по режиму списка: со включённым
  // «показывать списанные» в одной выборке лежат и живые, и списанные машины,
  // и предлагать «списать» тому, кто отметил уже списанную, — обман.
  const activeChosen = chosen.filter((r) => r.status === 'operational').length;
  const archivedChosen = chosen.length - activeChosen;
  const fleetFreshness = fleetFreshnessLabel(freshness);
  // Колонка «Причина» появляется только когда списанные ВИДНЫ: у машины в
  // парке причины выбытия нет по определению, и пустой столбец на постоянном
  // экране — это шум, который человек учится не читать.
  const showReason = includeArchived;

  return (
    <Stack gap={4}>
      <Tabs value={view} onValueChange={(v) => onViewChange(v as MachineryView)}>
        <TabsList>
          <TabsTrigger value="units">Единицы ({counts?.total ?? 0})</TabsTrigger>
          <TabsTrigger value="aggregates">Сцепки</TabsTrigger>
          <TabsTrigger value="service">ТО и ремонты</TabsTrigger>
        </TabsList>
      </Tabs>

      {view === 'aggregates' && <MachineryUnitsPanel farmId={farmId} canWrite={canWrite} />}

      {view === 'service' && (
        <Card>
          <CardContent className="p-4 text-sm text-muted-foreground">
            ТО и ремонты — следующий заход. Пока состояние машины выражено
            одним: она в парке или списана.
          </CardContent>
        </Card>
      )}

      {view === 'units' && (
        <>
          {/* Шапка «Актуальность парка» (Р29). Стоит ВЫШЕ фильтров и таблицы:
              это то, чем человек меряет доверие ко всему, что ниже. */}
          <div
            className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-muted/40 p-3 text-sm"
            data-testid="machinery-freshness"
          >
            <span className={fleetFreshness.stale ? 'text-destructive' : 'text-muted-foreground'}>
              {fleetFreshness.text}
            </span>
            {fleetFreshness.stale && (
              <Badge variant="outline" className="border-destructive text-destructive">
                Давно не подтверждали
              </Badge>
            )}
            <div className="flex-1" />
            {canWrite && (
              <Button
                size="sm"
                variant="outline"
                disabled={confirming}
                onClick={onConfirmFleet}
                data-testid="machinery-confirm-fleet"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                {confirming ? 'Отмечаю…' : 'Подтвердить актуальность'}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={search}
              placeholder="Поиск по модели или номеру"
              className="max-w-xs"
              onChange={(e) => onSearchChange(e.target.value)}
              data-testid="machinery-search"
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={group === null ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => onGroupChange(null)}
              >
                Все типы
              </Button>
              {Object.entries(MACHINERY_GROUP_LABELS).map(([code, label]) => (
                <Button
                  key={code}
                  variant={group === code ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => onGroupChange(code)}
                >
                  {label}
                </Button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox
                checked={includeArchived}
                onCheckedChange={(v) => onIncludeArchivedChange(v === true)}
                aria-label="Показывать списанные"
                data-testid="machinery-show-archived"
              />
              Показывать списанные
              {counts && counts.written_off > 0 && (
                <span className="text-xs">({counts.written_off})</span>
              )}
            </label>
            <div className="flex-1" />
            {canWrite && (
              <>
                {/* Обычная, не главная: это второй способ сделать то же самое,
                    а не промо (макет AK-804). */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onImport}
                  data-testid="machinery-import-open"
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Загрузить списком
                </Button>
                <Button size="sm" onClick={onAdd} data-testid="machinery-add">
                  <Plus className="mr-2 h-4 w-4" />
                  Добавить технику
                </Button>
              </>
            )}
          </div>

          {canWrite && chosen.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
              <span className="font-semibold">Выбрано {chosen.length}</span>
              {limitNotice && (
                <span className="text-muted-foreground" data-testid="machinery-batch-notice">
                  {limitNotice}
                </span>
              )}
              <div className="flex-1" />
              {activeChosen > 0 && (
                <>
                  <Button size="sm" variant="outline" disabled={busy} onClick={onReassign}>
                    <MoveRight className="mr-2 h-4 w-4" />
                    Перегнать…
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={onArchive}
                    data-testid="machinery-bulk-archive"
                  >
                    <Archive className="mr-2 h-4 w-4" />
                    Списать
                  </Button>
                </>
              )}
              {archivedChosen > 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={onRestore}
                  data-testid="machinery-bulk-restore"
                >
                  <ArchiveRestore className="mr-2 h-4 w-4" />
                  Вернуть в парк
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onToggleAll(ids, false)}>
                Снять выделение
              </Button>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-xs uppercase text-muted-foreground">
                      {canWrite && (
                        <th className="w-10 p-3">
                          <Checkbox
                            checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                            disabled={ids.length === 0}
                            onCheckedChange={(v) => onToggleAll(ids, v === true)}
                            aria-label="Выбрать показанную технику"
                          />
                        </th>
                      )}
                      <th className="p-3 text-left font-semibold">Модель</th>
                      <th className="p-3 text-left font-semibold">Тип</th>
                      <th className="p-3 text-left font-semibold">Гос. №</th>
                      <th className="p-3 text-left font-semibold">Инв. №</th>
                      <th className="p-3 text-right font-semibold">Л.с.</th>
                      <th className="p-3 text-left font-semibold">Сцепки</th>
                      {showReason && <th className="p-3 text-left font-semibold">Причина</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-b border-border/60 last:border-0"
                        data-testid="machinery-row"
                      >
                        {canWrite && (
                          <td className="p-3">
                            <Checkbox
                              checked={selected.has(row.id)}
                              onCheckedChange={() => onToggleRow(row.id)}
                              aria-label={`Выбрать ${row.name}`}
                            />
                          </td>
                        )}
                        <td className="p-3">
                          <span className="font-medium">{row.name}</span>
                          {row.status === 'written_off' && (
                            <Badge variant="outline" className="ml-2">
                              {row.status_label}
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="secondary">
                            {row.type_name ?? machineryGroupLabel(row.machinery_group)}
                          </Badge>
                        </td>
                        <td className="p-3">{row.reg_number ?? <Muted>—</Muted>}</td>
                        <td className="p-3">
                          {row.inventory_number ? (
                            <span className="text-muted-foreground">{row.inventory_number}</span>
                          ) : (
                            <Muted>—</Muted>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {row.power_hp ? NUM.format(row.power_hp) : <Muted>—</Muted>}
                        </td>
                        <td className="p-3 text-muted-foreground">
                          {unitCountLabel(row.unit_count)}
                        </td>
                        {showReason && (
                          <td className="p-3 text-muted-foreground">
                            <WriteOffReasonCell reason={row.archive_reason} />
                          </td>
                        )}
                      </tr>
                    ))}
                    {!loading && rows.length === 0 && (
                      <tr>
                        <td
                          colSpan={(canWrite ? 7 : 6) + (showReason ? 1 : 0)}
                          className="p-6 text-center text-sm text-muted-foreground"
                        >
                          {search.trim() || group
                            ? 'Под фильтр ничего не попало.'
                            : 'Парк пуст — добавьте первую машину, и по ней посчитается выполнимость плана.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center gap-3 border-t border-border p-3 text-xs text-muted-foreground">
                <span>{loading ? 'Загружаю…' : `Показано ${rows.length} из ${total}`}</span>
                {hasMore && (
                  // Без этой кнопки список молча обрывался бы на первой
                  // странице — «показано 200 из 640 и идти некуда».
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loadingMore}
                    onClick={onLoadMore}
                    data-testid="machinery-load-more"
                  >
                    {loadingMore ? 'Гружу…' : 'Показать ещё'}
                  </Button>
                )}
                <div className="flex-1" />
                {breakdown && (
                  // Разбивка — с сервера и про ВЕСЬ парк хозяйства, а не про
                  // показанную страницу (правило «итог считает выборку»).
                  <span data-testid="machinery-breakdown">Парк хозяйства: {breakdown}</span>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground">{children}</span>;
}

/**
 * Причина списания в строке: короткая подпись + комментарий подписью.
 *
 * Списания до AK-764 причины не знают — у них прочерк, а не выдуманный код:
 * «Продана» напротив машины, про которую мы этого не знаем, хуже пустоты.
 */
function WriteOffReasonCell({ reason }: { reason: string | null }) {
  const parsed = splitWriteOffReason(reason);
  if (!parsed.label) return <Muted>—</Muted>;
  return (
    <span>
      {parsed.label}
      {parsed.comment && (
        <span className="ml-1 text-xs text-muted-foreground">· {parsed.comment}</span>
      )}
    </span>
  );
}
